import React, { useEffect } from 'react';
import { Modal, Card, Row, Col, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getSurveyUserFormDetail, clearSurveyUserFormDetail } from '../../../../redux/survey/survey_user_form/actions';
import { renderAnswerValue, getAnswerTypeColor } from '../../utils/surveyResponseUtils';

const SurveyResponseDetailModal = ({ show, onHide, surveyUserId, surveyId }) => {
    const dispatch = useDispatch();
    const { 
        surveyUserFormDetail, 
        isLoadingSurveyUserFormDetail, 
        surveyUserFormDetailError 
    } = useSelector(state => state.surveyUserForm);

    useEffect(() => {
        if (show && surveyUserId && surveyId) {
            dispatch(getSurveyUserFormDetail(surveyUserId, surveyId));
        } else if (!show) {
            // Limpiar los datos cuando se cierre el modal
            dispatch(clearSurveyUserFormDetail());
        }
    }, [show, surveyUserId, surveyId, dispatch]);

    return (
        <Modal show={show} onHide={onHide} size="xl" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Detalles de Respuestas de Encuesta</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh' }}>
                {isLoadingSurveyUserFormDetail ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3">Cargando detalles de la encuesta...</p>
                    </div>
                ) : surveyUserFormDetailError ? (
                    <Alert variant="danger">
                        <Alert.Heading>Error al cargar los detalles</Alert.Heading>
                        <p>{surveyUserFormDetailError}</p>
                    </Alert>
                ) : surveyUserFormDetail ? (
                    <div>
                        {/* Estadísticas */}
                        {surveyUserFormDetail.results && surveyUserFormDetail.results.length > 0 && (
                            <Card className="mb-3">
                                <Card.Header className="bg-warning text-dark">
                                    <h5 className="mb-0">
                                        <svg width="20" height="20" fill="currentColor" className="me-2" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                            <path d="M4 11H2v3h2v-3zm5-4H7v7h2V7zm5-5v12h-2V2h2zm-2-1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1h-2zM6 7a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7zM1 11a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-3z"/>
                                        </svg>
                                        Estadísticas de Respuestas
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    {(() => {
                                        const totalQuestions = surveyUserFormDetail.results.length;
                                        const answeredQuestions = surveyUserFormDetail.results.filter(result => {
                                            // Verificar según el tipo de pregunta donde debe estar la respuesta
                                            switch (result.type) {
                                                case 'Opción múltiple: solo una respuesta':
                                                case 'Opción múltiple: se permiten varias respuestas':
                                                    return result.options && result.options.some(opt => opt.selected);
                                                case 'Cuadro de texto de una línea':
                                                case 'Cuadro de texto con varias líneas':
                                                    return result.answer_text && result.answer_text.trim() !== '';
                                                case 'Valor numérico':
                                                    return result.answer_number !== null && result.answer_number !== undefined;
                                                case 'Fecha':
                                                    return result.answer_date && result.answer_date.trim() !== '';
                                                case 'Fecha y hora':
                                                    return result.answer_datetime && result.answer_datetime.trim() !== '';
                                                case 'Matriz':
                                                    return result.matrix_answers && result.matrix_answers.length > 0;
                                                default:
                                                    return false;
                                            }
                                        }).length;
                                        const completionPercentage = totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;
                                        return (
                                            <Row>
                                                <Col md={3}>
                                                    <div className="text-center">
                                                        <h4 className="text-primary">{totalQuestions}</h4>
                                                        <small className="text-muted">Total de Preguntas</small>
                                                    </div>
                                                </Col>
                                                <Col md={3}>
                                                    <div className="text-center">
                                                        <h4 className="text-success">{answeredQuestions}</h4>
                                                        <small className="text-muted">Respondidas</small>
                                                    </div>
                                                </Col>
                                                <Col md={3}>
                                                    <div className="text-center">
                                                        <h4 className="text-danger">{totalQuestions - answeredQuestions}</h4>
                                                        <small className="text-muted">Sin Responder</small>
                                                    </div>
                                                </Col>
                                                <Col md={3}>
                                                    <div className="text-center">
                                                        <h4 className="text-info">{completionPercentage}%</h4>
                                                        <small className="text-muted">Completado</small>
                                                    </div>
                                                </Col>
                                            </Row>
                                        );
                                    })()}
                                </Card.Body>
                            </Card>
                        )}
                        {/* Información de la Encuesta */}
                        <Card className="mb-3">
                            <Card.Header className="bg-primary text-white">
                                <h5 className="mb-0">
                                    <svg width="20" height="20" fill="currentColor" className="me-2" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                        <path d="M2.5 12a.5.5 0 0 1-.5-.5v-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-.5.5h-1zm3-4a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-1zm3-3a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1.5a.5.5 0 0 1-.5.5h-1zm3 0a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v2.5a.5.5 0 0 1-.5.5h-1z"/>
                                    </svg>
                                    Información de la Encuesta
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <strong>Nombre:</strong> 
                                        <span className="ms-2">{surveyUserFormDetail.survey?.name || 'N/A'}</span>
                                    </Col>
                                    <Col md={6}>
                                        <strong>ID:</strong> 
                                        <span className="ms-2">{surveyUserFormDetail.survey?.id || 'N/A'}</span>
                                    </Col>
                                </Row>
                                {surveyUserFormDetail.survey?.description && (
                                    <Row className="mt-2">
                                        <Col md={12}>
                                            <strong>Descripción:</strong>
                                            <div className="mt-1 p-2 bg-light rounded">
                                                {surveyUserFormDetail.survey.description}
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Información del Usuario */}
                        <Card className="mb-3">
                            <Card.Header className="bg-info text-white">
                                <h5 className="mb-0">
                                    <svg width="20" height="20" fill="currentColor" className="me-2" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                        <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                                    </svg>
                                    Información del Usuario
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <strong>Nombre Completo:</strong>
                                        <span className="ms-2">
                                            {surveyUserFormDetail.user?.first_name || ''} {surveyUserFormDetail.user?.last_name || ''}
                                        </span>
                                    </Col>
                                    <Col md={6}>
                                        <strong>Usuario:</strong>
                                        <span className="ms-2">{surveyUserFormDetail.user?.username || 'N/A'}</span>
                                    </Col>
                                </Row>
                                <Row className="mt-2">
                                    <Col md={6}>
                                        <strong>ID Usuario:</strong>
                                        <span className="ms-2">{surveyUserFormDetail.user?.id || 'N/A'}</span>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Respuestas */}
                        <Card>
                            <Card.Header className="bg-success text-white">
                                <h5 className="mb-0">
                                    <svg width="20" height="20" fill="currentColor" className="me-2" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                        <path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
                                        <path d="m2.165 15.803.02-.004c1.83-.363 2.948-.842 3.468-1.105A9.06 9.06 0 0 0 8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6a10.437 10.437 0 0 1-.524 2.318l-.003.011a10.722 10.722 0 0 1-.244.637c-.079.186.074.394.273.362a21.673 21.673 0 0 0 .693-.125zm.8-3.108a1 1 0 0 0-.287-.801C1.618 10.83 1 9.468 1 8c0-3.192 3.004-6 7-6s7 2.808 7 6c0 3.193-3.004 6-7 6a8.06 8.06 0 0 1-2.088-.272 1 1 0 0 0-.711.074c-.387.196-1.24.57-2.634.893a19.492 19.492 0 0 0 .398-2z"/>
                                    </svg>
                                    Preguntas y Respuestas ({surveyUserFormDetail.results?.length || 0} preguntas)
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                {surveyUserFormDetail.results && surveyUserFormDetail.results.length > 0 ? (
                                    surveyUserFormDetail.results.map((result, index) => (
                                        <Card key={result.question_id || index} className="mb-3 border-start border-4 border-primary">
                                            <Card.Header className="bg-light">
                                                <Row>
                                                    <Col md={8}>
                                                        <strong>Pregunta {result.secuence}:</strong>
                                                        <div className="mt-1">{result.question || 'Pregunta sin texto'}</div>
                                                    </Col>
                                                    <Col md={4} className="text-end">
                                                        <Badge bg={getAnswerTypeColor(result.type)}>
                                                            {result.type || 'Tipo no definido'}
                                                        </Badge>
                                                    </Col>
                                                </Row>
                                                {result.description && (
                                                    <small className="text-muted d-block mt-1">
                                                        <svg width="14" height="14" fill="currentColor" className="me-1" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                                            <path d="M2 2a1 1 0 0 1 1-1h4.586a1 1 0 0 1 .707.293l7 7a1 1 0 0 1 0 1.414l-4.586 4.586a1 1 0 0 1-1.414 0l-7-7A1 1 0 0 1 2 6.586V2zm3.5 4a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"/>
                                                        </svg>
                                                        Categoría: {result.description}
                                                    </small>
                                                )}
                                            </Card.Header>
                                            <Card.Body>
                                                {/* Mostrar opciones disponibles si existen */}
                                                {result.options && result.options.length > 0 && (
                                                    <div className="mb-3">
                                                        <strong>Opciones disponibles:</strong>
                                                        <div className="mt-2 d-flex flex-wrap gap-2">
                                                            {result.options.map((option, optionIndex) => (
                                                                <Badge 
                                                                    key={option.id || optionIndex} 
                                                                    bg={option.selected ? "success" : "outline-info"}
                                                                    className="px-2 py-1"
                                                                    style={option.selected ? {} : { border: '1px solid #0dcaf0', color: '#0dcaf0', backgroundColor: 'transparent' }}
                                                                >
                                                                    {option.selected && (
                                                                        <svg width="12" height="12" fill="currentColor" className="me-1" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                                                            <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.061L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                                                        </svg>
                                                                    )}
                                                                    {option.description}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                <div className="mt-2">
                                                    <strong>Respuesta completa:</strong>
                                                    <div className="mt-2 p-3 bg-light rounded border-start border-3 border-success">
                                                        {renderAnswerValue(result)}
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))
                                ) : (
                                    <Alert variant="info">
                                        <Alert.Heading>Sin preguntas</Alert.Heading>
                                        <p>No se encontraron preguntas para esta encuesta.</p>
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                ) : (
                    <Alert variant="warning">
                        <Alert.Heading>Sin datos</Alert.Heading>
                        <p>No se pudieron cargar los detalles de la encuesta.</p>
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    <svg width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                        <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                    </svg>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SurveyResponseDetailModal;